import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode, type EventCardId, type OfType } from '@reconcept-platform/shared/data-access';
import {
  type CreateEventCardInput,
  type CreateEventCardMutation,
  type CreateEventCardMutationVariables,
  type DestroyEventCardMutation,
  type DestroyEventCardMutationVariables,
  type DuplicateEventCardMutation,
  type DuplicateEventCardMutationVariables,
  type EventCardItemQueryQuery,
  type EventCardItemQueryQueryVariables,
  type EventCardTilesForExportQueryQuery,
  type EventCardTilesForExportQueryQueryVariables,
  type EventCardTilesQueryQuery,
  type EventCardTilesQueryQueryVariables,
  type EventCardWithCommentsQuery,
  type EventCardWithCommentsQueryVariables,
  type EventCardWithLinkedCardsQueryQuery,
  type EventCardWithLinkedCardsQueryQueryVariables,
  type TimelineWithEventCardsQueryQuery,
  type TimelineWithEventCardsQueryQueryVariables,
  type UpdateEventCardInput,
  type UpdateEventCardMutation,
  type UpdateEventCardMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { type EventCardStatusEnum } from '../../schema/schema-enums';
import { invalidateWorkspace } from '../../utils/cache-invalidators.utils';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { EventCardLogsQuery } from '../log/log.queries';
import { ViewerWithCardsAndTasksQuery } from '../viewer/viewer.queries';
import { eventCardItemFragment } from './event-card.fragments';
import {
  EventCardItemQuery,
  EventCardTilesForExportQuery,
  EventCardTilesQuery,
  eventCardWithCommentsQuery,
  EventCardWithLinkedCardsQuery,
  TimelineWithEventCardsQuery,
} from './event-card.queries';

export type EventCardWithLinkedCardsQuery_LinkedCard = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  EventCardWithLinkedCardsQueryQuery['node'],
  'EventCard'
  // @ts-expect-error TS2537 cannot index
>['linkedCards'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type EventCardItemQuery_EventCard = OfType<EventCardItemQueryQuery['node'], 'EventCard'>;
// @ts-expect-error TS2537 cannot index
export type EventCardItemQuery_LinkedCard = EventCardItemQuery_EventCard['linkedCards'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type EventCardWithCommentsQuery_EventCard = OfType<EventCardWithCommentsQuery['node'], 'EventCard'>;

// @ts-expect-error TS2339 property does not exist
export type TimelineWithEventCardsQuery_EventCard = TimelineWithEventCardsQueryQuery['eventCards']['nodes'][number];

// @ts-expect-error TS2339 property does not exist
export type EventCardTilesQuery_EventCard = EventCardTilesQueryQuery['eventCards']['nodes'][number];

// @ts-expect-error TS2339 property does not exist
export type EventCardTilesForExportQuery_EventCard = EventCardTilesForExportQueryQuery['eventCards']['nodes'][number];

@Injectable({
  providedIn: 'root',
})
export class EventCardApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  createEventCard(createEventCardInput: CreateEventCardInput): Observable<EventCardId> {
    // @ts-expect-error TS2322 type not assignable
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateEventCardMutation, CreateEventCardMutationVariables>({
          mutation: gql`
            mutation createEventCard($input: CreateEventCardInput!) {
              createEventCard(input: $input) {
                eventCard {
                  ...EventCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${eventCardItemFragment}
          `,
          variables: {
            input: createEventCardInput,
          },
          // the workspaceId is known here, but not which dashboard(s) show it, so refetch by operation name instead
          refetchQueries: ['DashboardQualityCalendar', 'WorkspaceWithQualityCalendar'],
          update: (cache) => {
            invalidateWorkspace(createEventCardInput.workspaceId, ['qualityInstruments'])(cache);

            cache.modify({
              fields: {
                eventCards(existingEventCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createEventCard.eventCard))
    );
  }

  watchEventCard(eventCardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<EventCardItemQueryQuery, EventCardItemQueryQueryVariables>({
        query: EventCardItemQuery,
        variables: {
          id: eventCardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'EventCard')));
  }

  watchEventCards(eventCardsFilter: EventCardTilesQueryQueryVariables) {
    return (
      this.workspaceGraphQLService
        .watchQuery<EventCardTilesQueryQuery, EventCardTilesQueryQueryVariables>({
          query: EventCardTilesQuery,
          variables: {
            workspaceIds: eventCardsFilter.workspaceIds,
            status: eventCardsFilter.status,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.eventCards.nodes))
    );
  }

  watchEventCardsForExport(variables: EventCardTilesForExportQueryQueryVariables) {
    return (
      this.workspaceGraphQLService
        .watchQuery<EventCardTilesForExportQueryQuery, EventCardTilesForExportQueryQueryVariables>({
          query: EventCardTilesForExportQuery,
          variables,
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.eventCards.nodes))
    );
  }

  watchTimelineEventCards(status: EventCardStatusEnum[], workspaceIds: string[]) {
    return (
      this.workspaceGraphQLService
        .watchQuery<TimelineWithEventCardsQueryQuery, TimelineWithEventCardsQueryQueryVariables>({
          query: TimelineWithEventCardsQuery,
          variables: {
            workspaceIds,
            status,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.eventCards.nodes))
    );
  }

  watchEventWithLinkedCards(eventCardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<EventCardWithLinkedCardsQueryQuery, EventCardWithLinkedCardsQueryQueryVariables>({
        query: EventCardWithLinkedCardsQuery,
        variables: {
          id: eventCardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'EventCard')));
  }

  updateEventCard(updateEventCardInput: UpdateEventCardInput, workspaceId?: string) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateEventCardMutation, UpdateEventCardMutationVariables>({
          mutation: gql`
            mutation updateEventCard($input: UpdateEventCardInput!) {
              updateEventCard(input: $input) {
                eventCard {
                  ...EventCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${eventCardItemFragment}
          `,
          variables: {
            input: updateEventCardInput,
          },
          refetchQueries: [
            {
              query: EventCardItemQuery,
              variables: {
                id: updateEventCardInput.eventCardId,
              },
            },
            {
              query: EventCardLogsQuery,
              variables: {
                eventCardIds: [updateEventCardInput.eventCardId],
              },
            },
            {
              query: ViewerWithCardsAndTasksQuery,
            },
            // the workspaceId is known here, but not which dashboard(s) show it, so refetch by operation name instead
            'DashboardQualityCalendar',
            'WorkspaceWithQualityCalendar',
          ],
          update: (cache) => {
            if (workspaceId) {
              invalidateWorkspace(workspaceId, ['qualityInstruments'])(cache);
            }

            cache.modify({
              fields: {
                eventCards(existingEventCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateEventCard.eventCard))
    );
  }

  destroyEventCard($event: string, workspaceId: string) {
    return this.workspaceGraphQLService.mutateV2<DestroyEventCardMutation, DestroyEventCardMutationVariables>({
      mutation: gql`
        mutation DestroyEventCard($input: DestroyEventCardInput!) {
          destroyEventCard(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input: {
          eventCardId: $event,
        },
      },
      // the workspaceId is known here, but not which dashboard(s) show it, so refetch by operation name instead
      refetchQueries: ['DashboardQualityCalendar', 'WorkspaceWithQualityCalendar'],
      update: (cache) => {
        // invalidate quality instruments, so the deleted card is removed from the quality-calender screen
        invalidateWorkspace(workspaceId, ['qualityInstruments'])(cache);

        // delete other cards from cache, so linked cards are refetched
        cache.modify({
          fields: {
            eventCards(existingEventCards, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
    });
  }

  duplicateEventCard(eventCardId: string, workspaceId: string) {
    return (
      this.workspaceGraphQLService
        .mutateV2<DuplicateEventCardMutation, DuplicateEventCardMutationVariables>({
          mutation: gql`
            mutation DuplicateEventCard($input: DuplicateEventCardInput!) {
              duplicateEventCard(input: $input) {
                eventCard {
                  ...EventCardItemFragment
                }

                errors {
                  message
                  path
                }
              }
            }
            ${eventCardItemFragment}
          `,
          variables: {
            input: {
              eventCardId,
            },
          },
          // the workspaceId is known here, but not which dashboard(s) show it, so refetch by operation name instead
          refetchQueries: ['DashboardQualityCalendar', 'WorkspaceWithQualityCalendar'],
          update: (cache) => {
            invalidateWorkspace(workspaceId, ['qualityInstruments'])(cache);

            cache.modify({
              fields: {
                eventCards(existingEventCards, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.duplicateEventCard.eventCard))
    );
  }

  watchEventCardWithComments(cardId: string) {
    return this.workspaceGraphQLService
      .watchQuery<EventCardWithCommentsQuery, EventCardWithCommentsQueryVariables>({
        query: eventCardWithCommentsQuery,
        variables: {
          id: cardId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'EventCard')));
  }
}
